import React, { useState, useMemo, useCallback } from 'react';
import { useUpdateEffect } from 'react-use';
import { makeStyles } from '@material-ui/core/styles';
import Link from 'next/link';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import GridContainer from '@/components/material-ui-kit/components/Grid/GridContainer';
import GridItem from '@/components/material-ui-kit/components/Grid/GridItem';
import Button from '@/components/material-ui-kit/components/CustomButtons/Button';
import Main from '@/layouts/Main';
import Head from '@/components/common/Head';
import CustomInput from '@/components/common/ui/CustomInput';
import styles from 'styles/jss/original/pages/contacts';
import meta from '@/config/meta';
import Joi from 'joi-browser';
import classNames from 'classnames';
import firebase from '@/lib/firebase/initFirebase';
import 'firebase/functions';
import basicAuthentication from '@/lib/basicAuthentication';
import useIsWebview from '@/hooks/useIsWebview';
import { wrapper } from '@/store/configureStore';

const useStyles = makeStyles(styles);

const ContactPage = () => {
  const [selectValue, setSelect] = useState('');
  const [validationForm, setValidationForm] = useState({
    nameValue: '',
    mailAddressValue: '',
    textAreaValue: ''
  });
  const [errors, setErrors] = useState({
    nameValue: false,
    mailAddressValue: false,
    textAreaValue: false
  });
  const [touched, setTouched] = useState({
    nameValue: false,
    mailAddressValue: false,
    textAreaValue: false
  });
  const [submitted, setSubmit] = useState(false);
  const [sending, setSending] = useState(false);
  const isWebView = useIsWebview();
  const selectList = useMemo(
    () => [
      {
        title: 'お問い合わせ種類',
        value: null,
        disabled: true
      },
      {
        title: '使い方について',
        value: '使い方について',
        disabled: false
      },
      {
        title: '掲載記事や商品について',
        value: '掲載記事や商品について',
        disabled: false
      },
      {
        title: 'アカウントについて',
        value: 'アカウントについて',
        disabled: false
      },
      {
        title: 'その他',
        value: 'その他',
        disabled: false
      }
    ],
    []
  );
  const classes = useStyles();
  const isValid =
    selectValue &&
    Object.keys(errors).every((key) => !errors[key]) &&
    Object.keys(touched).every((key) => touched[key]);
  const schema = Joi.object().keys({
    nameValue: Joi.string().required(),
    mailAddressValue: Joi.string()
      .email()
      .required(),
    textAreaValue: Joi.string().required()
  });
  const handleSelect = useCallback((event) => {
    setSelect(event.target.value);
  }, []);
  const handleValidationForm = useCallback(
    (target) => {
      setValidationForm(Object.assign({}, validationForm, target));
      setTouched(Object.assign({}, touched, { [Object.keys(target)[0]]: true }));
    },
    [touched, validationForm]
  );
  useUpdateEffect(() => {
    setErrors({
      nameValue: false,
      mailAddressValue: false,
      textAreaValue: false
    });
    const result = Joi.validate(validationForm, schema, { abortEarly: false });
    if (result.error) {
      result.error.details.forEach((detail) => {
        const name = detail.path[0];
        if (touched[name]) {
          setErrors(Object.assign({}, errors, { [name]: true }));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationForm]);
  const submit = useCallback(async () => {
    setSending(true);
    const functions = firebase.app().functions('asia-northeast1');
    const sendMail = functions.httpsCallable('sendMail');
    try {
      await sendMail({
        userContactType: selectValue,
        userName: validationForm.nameValue,
        userEmail: validationForm.mailAddressValue,
        userContactText: validationForm.textAreaValue
      });
      setSubmit(true);
    } catch (error) {
      throw new Error(error);
    } finally {
      setSending(false);
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
    }
  }, [selectValue, validationForm]);
  return (
    <div className={classes.page}>
      <Main>
        <Head
          title={meta.contact.title}
          description={meta.contact.description}
          image={meta.contact.image}
          url={meta.contact.url}
        />
        <div className={classes.main}>
          <div className={classes.section}>
            {!submitted ? (
              <div className={classes.container}>
                <h1 className={classes.titleLevel2}>お問い合わせ</h1>
                <GridContainer justify='center'>
                  <GridItem xs={12} sm={10} md={10}>
                    <FormControl fullWidth className={classes.selectFormControl}>
                      <InputLabel htmlFor='contact-type' className={classes.selectLabel}>
                        お問い合わせ種類
                      </InputLabel>
                      <Select
                        MenuProps={{
                          className: classes.selectMenu
                        }}
                        classes={{
                          select: classes.select
                        }}
                        value={selectValue}
                        onChange={handleSelect}
                        inputProps={{
                          name: 'contactSelect',
                          id: 'contact-type'
                        }}
                      >
                        {selectList.map((item) => {
                          return (
                            <MenuItem
                              disabled={item.disabled}
                              classes={{
                                root: classes.selectMenuItem,
                                selected: classes.selectMenuItemSelected
                              }}
                              key={item.value}
                              value={item.value && item.value}
                            >
                              {item.title}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </GridItem>
                  <GridItem xs={12} sm={10} md={10}>
                    <CustomInput
                      labelText='お名前'
                      id='name-input'
                      error={errors.nameValue}
                      success={touched.nameValue && !errors.nameValue}
                      inputProps={{
                        onChange: (event) =>
                          handleValidationForm({
                            nameValue: event.target.value
                          }),
                        value: validationForm.nameValue
                      }}
                      formControlProps={{
                        fullWidth: true
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={10} md={10}>
                    <CustomInput
                      labelText='メールアドレス'
                      id='mail-address-input'
                      success={touched.mailAddressValue && !errors.mailAddressValue}
                      error={errors.mailAddressValue}
                      inputProps={{
                        onChange: (event) =>
                          handleValidationForm({
                            mailAddressValue: event.target.value
                          }),
                        value: validationForm.mailAddressValue
                      }}
                      formControlProps={{
                        fullWidth: true
                      }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={10} md={10}>
                    <CustomInput
                      labelText='お問い合わせ内容'
                      id='textarea-input'
                      error={errors.textAreaValue}
                      success={touched.textAreaValue && !errors.textAreaValue}
                      inputProps={{
                        onChange: (event) =>
                          handleValidationForm({
                            textAreaValue: event.target.value
                          }),
                        value: validationForm.textAreaValue,
                        multiline: true,
                        rows: 5
                      }}
                      formControlProps={{
                        fullWidth: true
                      }}
                    />
                  </GridItem>
                  <GridItem className={classes.textCenter}>
                    <Button
                      color='warning'
                      round
                      onClick={submit}
                      disabled={!isValid || sending}
                      className={classes.submitButton}
                    >
                      送信する
                    </Button>
                  </GridItem>
                </GridContainer>
              </div>
            ) : (
              <div className={classes.container}>
                <h1 className={classes.titleLevel2}>送信完了</h1>
                <h5 className={classes.description}>お問い合わせフォームの送信が完了しました</h5>
                {isWebView ? null : (
                  <GridContainer>
                    <GridItem
                      md={4}
                      sm={4}
                      className={classNames(classes.mrAuto, classes.mlAuto, classes.textCenter)}
                    >
                      <Link href='/'>
                        <a>
                          <Button color='warning' round>
                            ホームへ移動する
                          </Button>
                        </a>
                      </Link>
                    </GridItem>
                  </GridContainer>
                )}
              </div>
            )}
          </div>
        </div>
      </Main>
    </div>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(async ({ req, res }) => {
  if (!process.browser) {
    basicAuthentication({ req, res });
  }
});

export default ContactPage;
